import { Controller } from "@hotwired/stimulus"
import { defaultAssistantName } from "../utils/brand"

const NAMES = [
    "Aiko", "Alex", "Amara", "Atlas", "Elio", "Iris", "Juno", "Kai",
    "Mara", "Marlowe", "Max", "Mira", "Nova", "Nyx", "Orion", "Pip",
    "Remy", "Sage", "Sloane", "Tali", "Theo", "Vera", "Wren", "Zara"
]

// Deliberate, coherent starting characters for each renderer. These are not
// exhaustive templates: they set the high-signal visual and personality fields
// and leave the rest on the classic defaults, making every style dependable
// while the main dice action remains genuinely random.
const STYLE_PRESETS = {
    photorealistic: { name: "Jane", pronouns: "she_her", age_band: "twenties", being_type: "human", complexion: "medium", hair_color: "dark_brown", hair_length: "long", hair_texture: "wavy", wardrobe: "smart_casual", lighting: "natural", backdrop: "cafe", framing: "head_and_shoulders", archetype: "cheerleader", energy: "upbeat" },
    cinematic: { name: "Sloane", pronouns: "she_her", age_band: "thirties", being_type: "human", face_shape: "angular", eye_color: "amber", hair_color: "black", hair_length: "medium", hair_texture: "wavy", build: "athletic", wardrobe: "smart_casual", palette: "neutral", lighting: "golden_hour", backdrop: "city", framing: "half_body", archetype: "strategist", energy: "steady" },
    cyberpunk: { name: "Nyx", pronouns: "they_them", age_band: "ageless", being_type: "android", eye_color: "violet", hair_color: "vivid", hair_length: "short", hair_texture: "undercut", wardrobe: "techwear", palette: "bold", lighting: "neon", backdrop: "city", framing: "half_body", archetype: "operator", energy: "high" },
    anime: { name: "Aiko", pronouns: "she_her", age_band: "twenties", being_type: "human", eye_color: "blue", hair_color: "black", hair_length: "very_long", hair_texture: "straight", wardrobe: "streetwear", palette: "pastel", lighting: "soft", backdrop: "city", framing: "portrait", archetype: "best_friend", energy: "upbeat" },
    painterly: { name: "Elio", pronouns: "he_him", age_band: "thirties", being_type: "human", eye_color: "hazel", hair_color: "brown", hair_length: "medium", hair_texture: "curly", wardrobe: "bohemian", palette: "earth", lighting: "golden_hour", backdrop: "forest", framing: "half_body", archetype: "muse", energy: "calm" },
    comic: { name: "Max", pronouns: "they_them", age_band: "twenties", being_type: "human", eye_color: "green", hair_color: "auburn", hair_length: "short", hair_texture: "wavy", build: "athletic", wardrobe: "streetwear", palette: "bold", lighting: "dramatic", backdrop: "city", framing: "half_body", archetype: "wildcard", energy: "high" },
    toon_3d: { name: "Pip", pronouns: "they_them", age_band: "ageless", being_type: "elf_fae", eye_color: "violet", hair_color: "pastel", hair_length: "short", hair_texture: "curly", wardrobe: "cottagecore", palette: "pastel", lighting: "soft", backdrop: "forest", framing: "full_body", archetype: "cheerleader", energy: "upbeat" },
    watercolor: { name: "Wren", pronouns: "she_her", age_band: "mature", being_type: "human", eye_color: "grey", hair_color: "grey", hair_length: "medium", hair_texture: "wavy", wardrobe: "bohemian", palette: "cool", lighting: "natural", backdrop: "beach", framing: "portrait", archetype: "sage", energy: "calm" },
    pixel: { name: "Nova", pronouns: "they_them", age_band: "ageless", being_type: "robot", eye_color: "blue", hair_color: "vivid", hair_length: "short", hair_texture: "buzz", wardrobe: "techwear", palette: "bold", lighting: "neon", backdrop: "space", framing: "full_body", archetype: "operator", energy: "high" },
    noir: { name: "Marlowe", pronouns: "he_him", age_band: "forties", being_type: "human", face_shape: "square", eye_color: "grey", hair_color: "black", hair_length: "short", hair_texture: "straight", wardrobe: "formal", palette: "monochrome", lighting: "moody", backdrop: "city", framing: "head_and_shoulders", archetype: "strategist", energy: "steady" }
}

// Animals, creatures and plush toys, as starting points beside the art styles.
// Each names its own art style; `species` is the free-text field the prompt reads.
const CREATURE_PRESETS = {
    biscuit: { art_style: "photorealistic", name: "Biscuit", being_type: "animal", species: "golden retriever", pronouns: "he_him", life_stage: "baby", coat_color: "cream", coat_texture: "fluffy", expression: "cheerful", accessory: "bandana", creature_eyes: "brown", lighting: "natural", backdrop: "cafe", framing: "full_body", archetype: "cheerleader", energy: "upbeat" },
    miso: { art_style: "anime", name: "Miso", being_type: "animal", species: "ginger tabby cat", pronouns: "she_her", life_stage: "young", coat_color: "ginger", coat_texture: "silky", coat_pattern: "striped", expression: "curious", creature_eyes: "green", lighting: "soft", backdrop: "library", framing: "half_body", archetype: "muse", energy: "calm" },
    hoot: { art_style: "painterly", name: "Hoot", being_type: "animal", species: "barn owl", pronouns: "he_him", coat_color: "cream", coat_texture: "feathered", expression: "calm", accessory: "glasses", lighting: "moody", backdrop: "library", framing: "portrait", archetype: "sage", energy: "calm" },
    ember: { art_style: "toon_3d", name: "Ember", being_type: "creature", species: "small friendly dragon", pronouns: "they_them", life_stage: "young", coat_color: "vivid", coat_texture: "scaly", expression: "mischievous", creature_eyes: "amber", lighting: "golden_hour", backdrop: "forest", framing: "full_body", archetype: "wildcard", energy: "high" },
    waffles: { art_style: "photorealistic", name: "Waffles", being_type: "mascot", species: "teddy bear", pronouns: "he_him", coat_color: "brown", coat_texture: "plush", expression: "cheerful", accessory: "bow_tie", lighting: "soft", backdrop: "office", framing: "half_body", archetype: "best_friend", energy: "upbeat" },
    rusty: { art_style: "comic", name: "Rusty", being_type: "anthro", species: "fox", pronouns: "he_him", coat_color: "ginger", coat_texture: "fluffy", expression: "dignified", wardrobe: "business", lighting: "dramatic", backdrop: "city", framing: "half_body", archetype: "strategist", energy: "steady" }
}

// What to call a randomly rolled animal or creature (the species field is free
// text, so the dice has to supply one or the picture is "a friendly animal").
const RANDOM_SPECIES = {
    animal: ["golden retriever puppy", "tabby cat", "corgi", "red panda", "barn owl", "rabbit", "otter", "fox", "husky", "penguin"],
    creature: ["small friendly dragon", "unicorn", "phoenix", "griffin", "forest sprite", "baby kraken"],
    mascot: ["teddy bear", "plush bunny", "stuffed elephant", "knitted fox", "felt owl"],
    anthro: ["fox", "wolf", "cat", "bear", "rabbit", "owl"]
}

// Client-side dice + reset for the persona character sheet. Operates purely on
// the radio/checkbox/segment inputs already in the DOM (grouped by data
// attributes) so it needs no serialized vocab — no LLM, no network.
export default class extends Controller {
    static targets = ["name", "status", "undo", "saveStatus"]
    static values = { draft: Boolean, forms: Object }

    connect() {
        this.syncForm()
        this.baseline = this.snapshot()
        this.onUnload = (event) => {
            if (!this.dirty || this.isSubmitting) return
            event.preventDefault()
            event.returnValue = ""
        }
        window.addEventListener("beforeunload", this.onUnload)
        this.onVisit = (event) => {
            if (this.dirty && !this.isSubmitting && !window.confirm("Leave without saving your persona changes?")) event.preventDefault()
        }
        document.addEventListener("turbo:before-visit", this.onVisit)
    }

    disconnect() {
        window.removeEventListener("beforeunload", this.onUnload)
        document.removeEventListener("turbo:before-visit", this.onVisit)
    }

    // Only the editor's own fields: the photo and voice rows sit inside it
    // but belong to their own forms (form=), and aren't unsaved changes.
    snapshot() {
        return Array.from(this.element.querySelectorAll('#persona-editor input, #persona-editor textarea, #persona-editor select'))
            .filter((el) => el.form?.id === "persona-editor")
            .map((el) => ({ el, value: el.value, checked: el.checked }))
    }

    // The kind of body the chosen Being describes (humanoid / creature / anthro),
    // read from the checked chip, falling back to the form the page rendered with.
    currentForm() {
        const being = this.element.querySelector('input[name="assistant_persona[appearance][being_type]"]:checked')?.value
        return this.formsValue[being] || "humanoid"
    }

    // Show only the sections and traits that describe this kind of being: a dog
    // has a coat, not hair. Hidden ones keep their values (flip back to Human and
    // the face is still there) but the prompt never reads them.
    syncForm() {
        const form = this.currentForm()
        this.element.querySelectorAll("[data-persona-forms]").forEach((el) => {
            el.hidden = !el.dataset.personaForms.split(" ").includes(form)
        })
    }

    changed() {
        this.syncForm()
        this.dirty = this.baseline.some(({ el, value, checked }) => el.value !== value || el.checked !== checked)
        if (this.hasSaveStatusTarget) this.saveStatusTarget.textContent = this.dirty ? "Unsaved changes" : (this.draftValue ? "Draft · not yet created" : "All changes saved")
        this.element.querySelectorAll("details.settings-disclosure").forEach((section) => {
            const meta = section.querySelector(":scope > summary .settings-disclosure-meta")
            const inputs = Array.from(section.querySelectorAll("[data-persona-group] input:checked"))
                .filter((input) => input.closest("details") === section)
            const choices = inputs.filter((input) => input.value)
                .slice(0, 3).map((input) => input.closest("label").textContent.trim())
            if (meta && inputs.length) meta.textContent = choices.join(" · ") || "No preference"
        })
    }

    submitting() { this.isSubmitting = true }

    remember() {
        this.previous = this.snapshot()
        if (this.hasUndoTarget) this.undoTarget.hidden = false
    }

    undo() {
        if (!this.previous) return
        this.previous.forEach(({ el, value, checked }) => {
            el.value = value
            el.checked = checked
            el.dispatchEvent(new Event("change", { bubbles: true }))
        })
        this.previous = null
        this.undoTarget.hidden = true
        this.changed()
        this.announce("Starting point undone. Your previous choices are restored.")
    }

    // Roll the whole sheet: one random option per single-select group, a random
    // step per slider, and a small random subset per multi-select group.
    dice() {
        this.remember()
        this.element.querySelectorAll("[data-persona-group]").forEach((group) => {
            if (group.closest("[hidden]")) return
            this.pickRandomRadio(group)
        })
        this.rollSpecies()
        this.element.querySelectorAll("[data-persona-slider]").forEach((slider) => {
            this.pickRandomRadio(slider)
        })
        this.element.querySelectorAll("[data-persona-multi]").forEach((group) => {
            this.randomizeMulti(group)
        })
        this.rotateName()
        this.changed()
        this.announce(`Created a random starting point named ${this.nameTarget.value}.`)
    }

    // An animal or creature needs to be SOMETHING; a person does not.
    rollSpecies() {
        const being = this.element.querySelector('input[name="assistant_persona[appearance][being_type]"]:checked')?.value
        const choices = RANDOM_SPECIES[being]
        if (!choices) return
        this.setText("species", choices[Math.floor(Math.random() * choices.length)])
    }

    // A predictable option for every art style, and for the animal and creature
    // starting points. Start from the classic defaults
    // first, then layer in the smaller style-specific character definition.
    preset(event) {
        const style = event.params.style
        const found = STYLE_PRESETS[style] || CREATURE_PRESETS[style]
        if (!found) return
        // The classic preset carries the assistant's default name ("Jane"; "Lex" on Lex).
        const preset = found.name === "Jane" ? { ...found, name: defaultAssistantName() } : found

        const artStyle = preset.art_style || style
        this.remember()
        this.resetTraits()
        this.setRadio("assistant_persona[art_style]", artStyle)
        Object.entries(preset).forEach(([field, value]) => {
            if (field === "name" || field === "art_style") return
            if (field === "species") return this.setText(field, value)
            this.setTrait(field, value)
        })
        this.nameTarget.value = preset.name
        this.changed()
        this.announce(`Applied the ${preset.name} starting point (${artStyle.replaceAll("_", " ")}).`)
    }

    // Reset to "classic Jane": clear every selection back to its default — the
    // field's concrete default chip (data-persona-default) where it has one, else
    // the "Any" option, else the first chip — sliders to the middle step,
    // multi-selects to none, and the name/text fields empty.
    reset() {
        this.remember()
        this.resetTraits()
        this.changed()
        this.announce(`Classic ${defaultAssistantName()} applied. Your written details are preserved; Undo restores your previous choices.`)
    }

    resetTraits() {
        this.element.querySelectorAll("[data-persona-group]").forEach((group) => {
            const def = group.querySelector('input[type="radio"][data-persona-default="true"]')
            const blank = group.querySelector('input[type="radio"][value=""]')
            const target = def || blank || group.querySelector('input[type="radio"]')
            this.check(target)
        })
        this.element.querySelectorAll("[data-persona-slider]").forEach((slider) => {
            const radios = slider.querySelectorAll('input[type="radio"]')
            this.check(radios[Math.floor(radios.length / 2)])
        })
        this.element.querySelectorAll("[data-persona-multi]").forEach((group) => {
            group.querySelectorAll('input[type="checkbox"]').forEach((cb) => { cb.checked = false })
        })
        // Art style has no blank option — default to the first (photorealistic).
        this.element.querySelectorAll('input[name="assistant_persona[art_style]"]').forEach((el, i) => { el.checked = i === 0 })
        if (this.hasNameTarget) this.nameTarget.value = defaultAssistantName()
    }

    rotateName() {
        if (!this.hasNameTarget) return
        const current = this.nameTarget.value.trim().toLowerCase()
        const choices = NAMES.filter((name) => name.toLowerCase() !== current)
        this.nameTarget.value = choices[Math.floor(Math.random() * choices.length)] || NAMES[0]
        this.nameTarget.dispatchEvent(new Event("input", { bubbles: true }))
    }

    setTrait(field, value) {
        const selectors = [
            `input[name="assistant_persona[appearance][${field}]"]`,
            `input[name="assistant_persona[character][${field}]"]`
        ]
        const input = this.element.querySelector(selectors.map((selector) => `${selector}[value="${value}"]`).join(","))
        this.check(input)
    }

    setText(field, value) {
        const input = this.element.querySelector(`input[name="assistant_persona[appearance][${field}]"]`)
        if (!input) return
        input.value = value
        input.dispatchEvent(new Event("input", { bubbles: true }))
    }

    setRadio(name, value) {
        const inputs = Array.from(this.element.querySelectorAll(`input[type="radio"][value="${value}"]`))
        this.check(inputs.find((input) => input.getAttribute("name") === name))
    }

    announce(message) {
        if (this.hasStatusTarget) this.statusTarget.textContent = message
    }

    pickRandomRadio(group) {
        const radios = Array.from(group.querySelectorAll('input[type="radio"]'))
            .filter((el) => el.value !== "")
        if (radios.length === 0) return
        this.check(radios[Math.floor(Math.random() * radios.length)])
    }

    randomizeMulti(group) {
        const boxes = Array.from(group.querySelectorAll('input[type="checkbox"]'))
        if (boxes.length === 0) return
        const max = parseInt(group.dataset.personaMultiMax || "3", 10)
        const count = 1 + Math.floor(Math.random() * Math.min(max, boxes.length))
        const shuffled = boxes.slice().sort(() => Math.random() - 0.5)
        boxes.forEach((cb) => { cb.checked = false })
        shuffled.slice(0, count).forEach((cb) => { cb.checked = true })
    }

    check(input) {
        if (!input) return
        input.checked = true
        input.dispatchEvent(new Event("change", { bubbles: true }))
    }
}
